import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { BpcActuals, CreateBpcActuals, UpdateBpcActuals } from '@repo/backend/crt'
import {
  BPC_ACTUALS_CREATE_ERROR_TYPES,
  BPC_ACTUALS_KPI_CODES,
  PRODUCT_CATEGORIES,
  SUB_CHANNEL_VALUES,
} from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { monthIndex } from '@repo/zod-schemas'
import { z } from 'zod'

export const bpcActualsSearchSchema = z.object({
  keyword: z.string().optional(),
  ...datatableSearchSchema.shape,
})

export const bpcActualsDetailsSearchSchema = z.object({
  keyword: z.string().optional(),
  kpi: z.enum(BPC_ACTUALS_KPI_CODES).optional(),
  // Absent = every month, summed per row.
  month: monthIndex.optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  subChannel: z.array(z.enum(SUB_CHANNEL_VALUES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  marking: z.array(z.string()).optional(),
  ...datatableSearchSchema.shape,
})

export type BpcActualsSearch = z.infer<typeof bpcActualsSearchSchema>
export type BpcActualsDetailsSearch = z.infer<typeof bpcActualsDetailsSearchSchema>

export type { BpcActuals }

type UseCreateBpcActualsArgs = {
  queries: BpcActualsSearch
  handleOnSuccess: (bpcActualsId: number) => void
}

type UseDeleteBpcActualsArgs = QueryHandlers & {
  bpcActualsIds: number[]
  queries: BpcActualsSearch
}

type UseUpdateBpcActualsArgs = QueryHandlers & {
  bpcActualsId: number
}

async function getBpcActualsList(search: BpcActualsSearch) {
  const api = getApi()
  const res = await api.crt['bpc-actuals'].$get({
    query: stringifyValues(search),
  })

  return await res.json()
}

async function getBpcActualsById(id: string, queries: BpcActualsDetailsSearch = {}) {
  const api = getApi()
  const res = await api.crt['bpc-actuals'][':id'].$get({
    param: { id },
    query: stringifyValues(queries),
  })

  return await res.json()
}

export const bpcActualsByIdQueryOptions = (id: string, queries: BpcActualsDetailsSearch = {}) => ({
  queryKey: ['bpcActualsById', id, queries],
  queryFn: () => getBpcActualsById(id, queries),
})

export function useBpcActualsById(id: string, queries: BpcActualsDetailsSearch = {}) {
  return useQuery(bpcActualsByIdQueryOptions(id, queries))
}

export type BpcActualsWithDetails = Awaited<ReturnType<typeof getBpcActualsById>>

async function getCurrentBpcActuals(crtPeriod?: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['bpc-actuals'].current.$get(
    { query: stringifyValues({ crtPeriod }) },
    { init: { signal } },
  )
  return await res.json()
}

export const currentBpcActualsQueryOptions = (crtPeriod?: string) => ({
  queryKey: ['currentBpcActuals', crtPeriod ?? null] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getCurrentBpcActuals(crtPeriod, signal),
})

export function useCurrentBpcActuals(crtPeriod?: string) {
  return useQuery(currentBpcActualsQueryOptions(crtPeriod))
}

async function getBpcActualsDetailsRawCsv(id: string, signal?: AbortSignal) {
  return fetchCsvExport(`/crt/bpc-actuals/${id}/details/raw-export`, signal)
}

async function getBpcActualsDetailsFilteredCsv(
  id: string,
  queries: BpcActualsDetailsSearch,
  signal?: AbortSignal,
) {
  const queryParams = new URLSearchParams(stringifyValues(queries)).toString()
  return fetchCsvExport(`/crt/bpc-actuals/${id}/details/export?${queryParams}`, signal)
}

export const bpcActualsDetailsRawExportQueryOptions = (id: string) => ({
  queryKey: ['bpcActualsDetailsRawExport', id],
  queryFn: ({ signal }: { signal: AbortSignal }) => getBpcActualsDetailsRawCsv(id, signal),
})

export const bpcActualsDetailsExportQueryOptions = (
  id: string,
  queries: BpcActualsDetailsSearch = {},
) => ({
  queryKey: ['bpcActualsDetailsExport', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getBpcActualsDetailsFilteredCsv(id, queries, signal),
})

async function createBpcActuals(data: CreateBpcActuals) {
  const api = getApi()

  const res = await api.crt['bpc-actuals'].$post({
    json: data,
  })

  return await res.json()
}

async function updateBpcActuals(bpcActualsId: number, data: UpdateBpcActuals) {
  const api = getApi()

  await api.crt['bpc-actuals'][':id'].$patch({
    param: { id: String(bpcActualsId) },
    json: data,
  })
}

async function deleteBpcActuals(bpcActualsIds: number[]) {
  const api = getApi()

  await api.crt['bpc-actuals'].bulk.$delete({
    json: { ids: bpcActualsIds },
  })
}

export function useBpcActualsList(queries: BpcActualsSearch) {
  return useQuery({
    queryKey: ['bpcActuals', queries],
    queryFn: () => getBpcActualsList(queries),
  })
}

export function useCreateBpcActuals({ queries, handleOnSuccess }: UseCreateBpcActualsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['bpcActuals', queries],
    mutationFn: createBpcActuals,
    onSuccess: (bpcActuals) => {
      handleOnSuccess(bpcActuals.id)

      queryClient.invalidateQueries({
        queryKey: ['bpcActuals', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('bpcActuals.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const customParams = issues?.find((i) => i.code === 'custom')?.params as
        | { type?: string; year?: number }
        | undefined

      let description: string
      switch (customParams?.type) {
        case BPC_ACTUALS_CREATE_ERROR_TYPES.yearConflict:
          description = `${t('errors.bpcActuals.createFailed', { ns: 'crt' })}: ${t(
            'errors.bpcActuals.yearConflict',
            { ns: 'crt', year: customParams.year },
          )}`
          break
        default:
          description = getFirstZodErrorMessage(issues, t, 'errors.bpcActuals.createFailed', 'crt')
      }

      toast.error(t('toast.error', { ns: 'common' }), {
        description,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateBpcActuals({ bpcActualsId, handleOnSuccess }: UseUpdateBpcActualsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateBpcActuals', bpcActualsId],
    mutationFn: (data: UpdateBpcActuals) => updateBpcActuals(bpcActualsId, data),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['bpcActualsById', String(bpcActualsId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('bpcActuals.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(issues, t, 'errors.bpcActuals.updateFailed', 'crt')

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteBpcActuals({
  bpcActualsIds,
  queries,
  handleOnSuccess,
}: UseDeleteBpcActualsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteBpcActuals'],
    mutationFn: () => deleteBpcActuals(bpcActualsIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['bpcActuals', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('bpcActuals.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(issues, t, 'errors.bpcActuals.deleteFailed', 'crt')

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
